import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Calendar, Users, AlertCircle, CalendarRange } from "lucide-react";
import {
  TRIP_DATE_LIFECYCLES,
  TRIP_DATE_LIFECYCLE_LABELS,
  TRIP_DATE_LIFECYCLE_BADGES,
  OCCUPANCY_LABELS,
  OCCUPANCY_BADGES,
  type TripDateLifecycle,
} from "@ckgo2/shared/types";
import { getSpotsBalance, occupancyLevel } from "@ckgo2/shared/utils";
import { enumLabel } from "@/i18n/enumLabels";
import { uiLocale } from "@/i18n";
import { formatMoney } from "@/lib/format";

export interface TripDate {
  id?: string;
  startDate: string;
  endDate: string;
  price: number;
  /** `null` = nevyplněná (jen u zájezdu bez horní hranice skupiny, jinak se nedá vymazat). */
  capacity: number | null;
  bookedCount?: number;
  lifecycle: TripDateLifecycle;
  guideId?: string;
  notes?: string;
  /**
   * Soukromý termín jedné objednávky zájezdu na vyžádání (F1.12). Editor ho jen ukazuje a
   * posílá zpět beze změny; `undefined` = veřejný termín (nový řádek z „Přidat termín“).
   */
  isPrivate?: boolean;
}

/** Štítek soukromého termínu; stejný v editoru termínů i v kartě termínu u poptávky. */
export function PrivateDateBadge() {
  const { t } = useTranslation();
  return (
    <span
      className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
      title={t("trips.dates.privateHint")}
    >
      {t("trips.dates.private")}
    </span>
  );
}

interface Props {
  value: TripDate[];
  onChange: (value: TripDate[]) => void;
  /**
   * Zájezd bez horní hranice skupiny: nový termín začíná BEZ kapacity (jinak natvrdo 20),
   * pole je povinné a vymazané zůstane prázdné (`null`), ne `0`.
   */
  openEnded?: boolean;
  /**
   * Chyba kapacity termínu na pozici `index` (nebo `null`) — zdroj (server / klientské
   * pravidlo) skládá vlastník pravidla, `DatesTab`.
   */
  capacityErrorAt?: (index: number) => string | null;
}

/**
 * Vstup pole kapacity. Bez horní hranice skupiny se vymazané pole NEdoplní nulou (zůstane
 * `null`) — jinak by prázdná kapacita prošla jako „vyprodáno".
 */
function parseCapacityInput(raw: string, openEnded: boolean): number | null {
  const parsed = parseInt(raw);
  if (openEnded) return Number.isNaN(parsed) ? null : parsed;
  return parsed || 0;
}

export default function TripDatesEditor({
  value,
  onChange,
  openEnded = false,
  capacityErrorAt,
}: Props) {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const addDate = () => {
    const today = new Date();
    const startDate = new Date(today.setMonth(today.getMonth() + 3));
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 14);

    const newDate: TripDate = {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
      price: 0,
      capacity: openEnded ? null : 20,
      bookedCount: 0,
      lifecycle: "tentative",
    };
    onChange([...value, newDate]);
    setExpandedIndex(value.length);
  };

  const updateDate = (index: number, updates: Partial<TripDate>) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], ...updates };
    onChange(newValue);
  };

  const removeDate = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(uiLocale(), {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="text-primary-500 h-5 w-5" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("trips.dates.title", { n: value.length })}
          </h3>
        </div>
        <button type="button" onClick={addDate} className="btn-ghost btn-sm text-primary-600">
          <Plus className="h-4 w-4" />
          {t("trips.dates.addDate")}
        </button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">{t("trips.dates.empty")}</p>
          <button type="button" onClick={addDate} className="btn-primary btn-sm mt-4">
            <Plus className="h-4 w-4" />
            {t("trips.dates.addFirstDate")}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((date, index) => {
            const capacityFieldError = capacityErrorAt?.(index) ?? null;
            const capacityMissing = Boolean(capacityFieldError);
            // Vadný termín se rozbalí sám — chybová věta patří k poli, ne do sbaleného řádku.
            const isExpanded = expandedIndex === index || capacityMissing;
            const duration = calculateDuration(date.startDate, date.endDate);
            const capacity = date.capacity ?? 0;
            // Provozní zůstatek BEZ ořezu: přeprodaný odjezd tu má být vidět záporně.
            const spotsLeft = getSpotsBalance(capacity, date.bookedCount || 0);
            const occupancy = occupancyLevel(date.bookedCount || 0, capacity);

            return (
              <div
                key={index}
                className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                {/* Header */}
                <div
                  className="flex cursor-pointer items-center gap-4 p-4"
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatDate(date.startDate)} - {formatDate(date.endDate)}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${TRIP_DATE_LIFECYCLE_BADGES[date.lifecycle]}`}
                      >
                        {enumLabel(TRIP_DATE_LIFECYCLE_LABELS, date.lifecycle)}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${OCCUPANCY_BADGES[occupancy]}`}
                        title={t("trips.dates.occupancyTitle")}
                      >
                        {enumLabel(OCCUPANCY_LABELS, occupancy)}
                      </span>
                      {date.isPrivate && <PrivateDateBadge />}
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                      <span>{t("trips.dates.days", { count: duration })}</span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {t("trips.dates.spots", {
                          left: spotsLeft,
                          capacity: date.capacity ?? "–",
                        })}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatMoney(date.price)}
                      </span>
                    </div>
                  </div>

                  {date.id && (
                    <Link
                      to={`/departures/${date.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1 rounded p-1 text-sm"
                      title={t("trips.dates.openVelin")}
                    >
                      <CalendarRange className="h-4 w-4" /> {t("trips.dates.velin")}
                    </Link>
                  )}

                  {/* Soukromý termín uložení zájezdu nesmaže (syncTripDates), takže koš by
                      řádek jen dočasně skryl; maže se na velínu nebo trasou termínu. */}
                  {!date.isPrivate && (
                    <button
                      title={t("common.actions.remove")}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDate(index);
                      }}
                      className="text-error-500 hover:text-error-600 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Content */}
                {isExpanded && (
                  <div className="space-y-4 border-t border-gray-200 p-4 dark:border-gray-700">
                    <div className="grid gap-4 sm:grid-cols-2">
                      {/* Start Date */}
                      <div>
                        <label className="label">{t("trips.dates.departureDate")}</label>
                        <input
                          type="date"
                          value={date.startDate}
                          onChange={(e) => updateDate(index, { startDate: e.target.value })}
                          className="input"
                        />
                      </div>

                      {/* End Date */}
                      <div>
                        <label className="label">{t("trips.dates.returnDate")}</label>
                        <input
                          type="date"
                          value={date.endDate}
                          onChange={(e) => updateDate(index, { endDate: e.target.value })}
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                      {/* Price */}
                      <div>
                        <label className="label">{t("trips.dates.priceAmount")}</label>
                        <input
                          type="number"
                          value={date.price}
                          onChange={(e) =>
                            updateDate(index, { price: parseInt(e.target.value) || 0 })
                          }
                          min={0}
                          step={100}
                          className="input"
                        />
                      </div>

                      {/* Capacity */}
                      <div>
                        <label className="label">{t("trips.dates.capacity")}</label>
                        <input
                          type="number"
                          value={date.capacity ?? ""}
                          onChange={(e) =>
                            updateDate(index, {
                              capacity: parseCapacityInput(e.target.value, openEnded),
                            })
                          }
                          min={1}
                          required={openEnded}
                          aria-invalid={capacityMissing || undefined}
                          className={`input ${capacityMissing ? "input-error" : ""}`}
                        />
                        {capacityMissing && (
                          <p className="text-error-600 mt-1 text-xs">{capacityFieldError}</p>
                        )}
                      </div>

                      {/* Booked Count */}
                      <div>
                        <label className="label">{t("trips.dates.booked")}</label>
                        <input
                          type="number"
                          value={date.bookedCount || 0}
                          onChange={(e) =>
                            updateDate(index, { bookedCount: parseInt(e.target.value) || 0 })
                          }
                          min={0}
                          max={date.capacity ?? undefined}
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      {/* Lifecycle (stored decision) — occupancy is derived, shown read-only above */}
                      <div>
                        <label className="label">{t("trips.dates.lifecycle")}</label>
                        <select
                          value={date.lifecycle}
                          onChange={(e) =>
                            updateDate(index, { lifecycle: e.target.value as TripDateLifecycle })
                          }
                          className="input"
                        >
                          {TRIP_DATE_LIFECYCLES.map((lc) => (
                            <option key={lc} value={lc}>
                              {enumLabel(TRIP_DATE_LIFECYCLE_LABELS, lc)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Guide assignment moved out of content editing (Block 4.2, R-A) — it's an
                          ops activity owned by the Schedule timeline + departure velín, so a content
                          save never touches the guide mirror. */}
                      <div>
                        <label className="label">{t("trips.form.guide")}</label>
                        <p className="text-sm text-gray-500">
                          {t("trips.dates.guideAssignedElsewhere")}
                        </p>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="label">{t("trips.dates.internalNotes")}</label>
                      <textarea
                        value={date.notes || ""}
                        onChange={(e) => updateDate(index, { notes: e.target.value })}
                        rows={2}
                        placeholder={t("trips.dates.internalNotesPlaceholder")}
                        className="input"
                      />
                    </div>

                    {/* Warning for low spots */}
                    {spotsLeft > 0 && spotsLeft <= 5 && (
                      <div className="bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400 flex items-center gap-2 rounded-lg p-3 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        {t("trips.dates.spotsLeftWarning", { count: spotsLeft })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
